import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createRole } from '../api/api';

const RoleCreatePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    requirements: '',
    weights: {
      years_of_experience: 20,
      role_relevant_experience: 30,
      education: 10,
      tech_stack: 30,
      loyalty: 10,
    },
    thresholds: {
      years_of_experience: 10,
      role_relevant_experience: 5,
      loyalty: 5,
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle weight change
  const handleWeightChange = (name, value) => {
    setFormData({
      ...formData,
      weights: {
        ...formData.weights,
        [name]: parseInt(value, 10),
      }
    });
  };

  // Handle threshold change
  const handleThresholdChange = (name, value) => {
    setFormData({
      ...formData,
      thresholds: {
        ...formData.thresholds,
        [name]: parseInt(value, 10),
      }
    });
  };

  // Validate total weight is 100%
  const isTotalWeightValid = () => {
    const total = Object.values(formData.weights).reduce((sum, weight) => sum + weight, 0);
    return total === 100;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title) {
      setError('Role title is required');
      return;
    }

    if (!isTotalWeightValid()) {
      setError('Total weight must be 100%');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare scoring criteria format for API
      const scoring_criteria = {
        years_of_experience: {
          weight: formData.weights.years_of_experience / 100,
          threshold: formData.thresholds.years_of_experience
        },
        role_relevant_experience: {
          weight: formData.weights.role_relevant_experience / 100,
          threshold: formData.thresholds.role_relevant_experience
        },
        education: {
          weight: formData.weights.education / 100
        },
        tech_stack: {
          weight: formData.weights.tech_stack / 100
        },
        loyalty: {
          weight: formData.weights.loyalty / 100,
          threshold: formData.thresholds.loyalty
        }
      };

      const roleData = {
        title: formData.title,
        requirements: formData.requirements,
        scoring_criteria
      };

      await createRole(roleData);
      navigate('/roles');
    } catch (err) {
      setError(err.detail || 'Error creating role. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8">
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Create New Job Role</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Role details */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">Role Details</h2>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Role Title*
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Senior Python Developer"
                  required
                />
              </div>
              <div>
                <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-1">
                  Role Requirements
                </label>
                <textarea
                  id="requirements"
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., 5+ years Python experience, Knowledge of Django, etc."
                  rows={4}
                />
              </div>
            </div>
          </div>

          {/* Scoring weights */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-gray-700">Scoring Criteria</h2>
              <div className={`text-sm ${isTotalWeightValid() ? 'text-green-600' : 'text-red-600'} font-medium`}>
                Total: {Object.values(formData.weights).reduce((sum, weight) => sum + weight, 0)}%
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Years of Experience */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="years_of_experience" className="text-sm font-medium text-gray-700">
                    Years of Experience
                  </label>
                  <span className="text-sm text-gray-500">{formData.weights.years_of_experience}%</span>
                </div>
                <input
                  type="range"
                  id="years_of_experience"
                  min="0"
                  max="100"
                  value={formData.weights.years_of_experience}
                  onChange={(e) => handleWeightChange('years_of_experience', e.target.value)}
                  className="w-full"
                />
                <div className="mt-2">
                  <label htmlFor="years_threshold" className="text-xs text-gray-500 block">
                    Threshold for full score:
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      id="years_threshold"
                      min="1"
                      max="30"
                      value={formData.thresholds.years_of_experience}
                      onChange={(e) => handleThresholdChange('years_of_experience', e.target.value)}
                      className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                    />
                    <span className="ml-2 text-xs text-gray-500">years</span>
                  </div>
                </div>
              </div>

              {/* Role-Relevant Experience */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="role_relevant_experience" className="text-sm font-medium text-gray-700">
                    Role-Relevant Experience
                  </label>
                  <span className="text-sm text-gray-500">{formData.weights.role_relevant_experience}%</span>
                </div>
                <input
                  type="range"
                  id="role_relevant_experience"
                  min="0"
                  max="100"
                  value={formData.weights.role_relevant_experience}
                  onChange={(e) => handleWeightChange('role_relevant_experience', e.target.value)}
                  className="w-full"
                />
                <div className="mt-2">
                  <label htmlFor="role_threshold" className="text-xs text-gray-500 block">
                    Threshold for full score:
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      id="role_threshold"
                      min="1"
                      max="20"
                      value={formData.thresholds.role_relevant_experience}
                      onChange={(e) => handleThresholdChange('role_relevant_experience', e.target.value)}
                      className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                    />
                    <span className="ml-2 text-xs text-gray-500">years</span>
                  </div>
                </div>
              </div>

              {/* Education */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="education" className="text-sm font-medium text-gray-700">
                    Education
                  </label>
                  <span className="text-sm text-gray-500">{formData.weights.education}%</span>
                </div>
                <input
                  type="range"
                  id="education"
                  min="0"
                  max="100"
                  value={formData.weights.education}
                  onChange={(e) => handleWeightChange('education', e.target.value)}
                  className="w-full"
                />
                <div className="mt-2">
                  <p className="text-xs text-gray-500">PhD = 100%, MSc/MBA = 50%, BSc = 30%</p>
                </div>
              </div>

              {/* Tech Stack */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="tech_stack" className="text-sm font-medium text-gray-700">
                    Tech Stack
                  </label>
                  <span className="text-sm text-gray-500">{formData.weights.tech_stack}%</span>
                </div>
                <input
                  type="range"
                  id="tech_stack"
                  min="0"
                  max="100"
                  value={formData.weights.tech_stack}
                  onChange={(e) => handleWeightChange('tech_stack', e.target.value)}
                  className="w-full"
                />
                <div className="mt-2">
                  <p className="text-xs text-gray-500">
                    Scored based on match with required technical skills
                  </p>
                </div>
              </div>

              {/* Loyalty */}
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="loyalty" className="text-sm font-medium text-gray-700">
                    Loyalty
                  </label>
                  <span className="text-sm text-gray-500">{formData.weights.loyalty}%</span>
                </div>
                <input
                  type="range"
                  id="loyalty"
                  min="0"
                  max="100"
                  value={formData.weights.loyalty}
                  onChange={(e) => handleWeightChange('loyalty', e.target.value)}
                  className="w-full"
                />
                <div className="mt-2">
                  <label htmlFor="loyalty_threshold" className="text-xs text-gray-500 block">
                    Threshold for full score:
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      id="loyalty_threshold"
                      min="1"
                      max="15"
                      value={formData.thresholds.loyalty}
                      onChange={(e) => handleThresholdChange('loyalty', e.target.value)}
                      className="w-16 border border-gray-300 rounded-md py-1 px-2 text-sm"
                    />
                    <span className="ml-2 text-xs text-gray-500">years at same company</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/roles')}
              className="py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`py-2 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 flex items-center ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isLoading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoleCreatePage;
